import html2canvas from 'html2canvas';
import 'blueimp-canvas-to-blob'; // HTMLCanvas toBlob pollyfill: for IE/Edge
import { Observable, from, of } from 'rxjs';
import { ScreenDimensions } from '../../models/screen.dimensions.model';
import { saveAs } from 'file-saver';
import { changeDpiBlob } from 'changedpi';
import { ImageTypeEnum } from '../../models/image-type.enum';

export namespace ExportImageUtils {
  export function exportImage(
    imageData: string | Blob,
    contentType: ImageTypeEnum,
    fileName: string,
    customDpi?: number,
  ): Observable<boolean> {
    const exportBlob =
      typeof imageData === 'string'
        ? ExportImageUtils.base64ToBlob(imageData, contentType)
        : imageData;

    // When publishing JPEG images for print using inches, the DPI should be recorded as 300 DPI in the JPEG header information.
    // HTML Canvas exports to 72 DPI, and does not allow a custom value, so we have to manually change the value in the blog file.
    // Does not apply to PNG, which does not store DPI information.
    if (customDpi) {
      changeDpiBlob(exportBlob, customDpi).then(function (dpiBlob) {
        saveAs(dpiBlob, fileName);
      });
    } else {
      saveAs(exportBlob, fileName);
    }

    return of(true);
  }

  export function canvasToBlob(
    canvas: HTMLCanvasElement,
    contentType: ImageTypeEnum,
    quality: number,
  ): Observable<Blob> {
    return new Observable((observer) => {
      canvas.toBlob(
        (blob) => {
          observer.next(blob);
          observer.complete();
        },
        contentType,
        quality,
      );
    });
  }

  export function fitCanvas(
    canvas: HTMLCanvasElement,
    { width, height }: ScreenDimensions,
  ): Observable<HTMLCanvasElement> {
    const thumbnailCanvas = document.createElement('canvas');
    thumbnailCanvas.width = width;
    thumbnailCanvas.height = height;

    const thumbnailContext = thumbnailCanvas.getContext('2d');

    // calculate source width and height dimensions
    const thumbanilAspect = thumbnailCanvas.width / thumbnailCanvas.height;
    const sourceHeight = canvas.height;
    const sourceWidth = thumbanilAspect * sourceHeight;

    // calculate source offset: clamp to 0 (fixes IE11 issue w/ negative offsets)
    const offsetX = Math.max(0.5 * (canvas.width - sourceWidth), 0);

    // render 3D frame and transfer to thumbnail canvas
    return new Observable((observer) => {
      thumbnailContext.drawImage(canvas, offsetX, 0, thumbnailCanvas.width, thumbnailCanvas.height);
      observer.next(thumbnailCanvas);
      observer.complete();
    });
  }

  export function elementToCanvas(
    element: HTMLElement,
    transparent = false,
  ): Observable<HTMLCanvasElement> {
    const options = transparent ? { backgroundColor: 'transparent' } : {};
    return from(
      html2canvas(element, {
        scale: 1,
        ...options,
      }),
    );
  }

  export function combineCanvasElements(canvasElements: HTMLCanvasElement[]): HTMLCanvasElement {
    // calculate max bounds
    const width = Math.max(0, ...canvasElements.map((canvas) => canvas.width));
    const height = Math.max(0, ...canvasElements.map((canvas) => canvas.height));

    const targetCanvas = document.createElement('canvas');
    targetCanvas.width = width;
    targetCanvas.height = height;

    // get 2D context for painting each image
    const targetContext = targetCanvas.getContext('2d');

    // paint each canvas to top left, ordered
    canvasElements.forEach((canvas) => {
      targetContext.drawImage(canvas, 0, 0);
    });

    return targetCanvas;
  }

  export function base64ToBlob(imageData: string, contentType: ImageTypeEnum): Blob {
    const byteString = atob(imageData.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: contentType });
  }

  export function blobToBase64(blob: Blob): Observable<string> {
    return new Observable((observer) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        observer.next(<string>reader.result);
        observer.complete();
      });
      reader.readAsDataURL(blob);
    });
  }

  export function blobToCanvas(
    blob: Blob,
    { width, height }: ScreenDimensions,
  ): Observable<HTMLCanvasElement> {
    return new Observable((observer) => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      const img = new Image();
      img.addEventListener('load', () => {
        context.drawImage(img, 0, 0);
        observer.next(canvas);
        observer.complete();
      });
      img.src = URL.createObjectURL(blob);
    });
  }
}
